import { supabase } from "../lib/supabaseClient";
import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

interface Notification {
  id: string;
  title: string;
  message: string;
  created_at: string;
  importance: string;
  trip_id: string | null;
  trip: { name: string } | null;
  author: { first_name: string; last_name: string } | null;
}

const ARCHIVE_MS = 14 * 86400000;

export default function ChatOverview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chats, setChats] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchivedChats, setShowArchivedChats] = useState(false);

  useEffect(() => {
    if (!user) return;

    const load = async () => {
      const [chatsResult, notifsResult] = await Promise.all([
        supabase.rpc("get_chats_with_unread_count", { user_id_param: user.id }),
        supabase
          .from("notifications")
          .select("id, title, message, created_at, importance, trip_id, trip:trips(name), author:profiles(first_name, last_name)")
          .not("read_by", "cs", `{${user.id}}`)
          .neq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);

      if (!chatsResult.error && chatsResult.data) {
        const tripIds = chatsResult.data.map((c: any) => c.trip_id);
        const { data: tripDates } = await supabase
          .from("trips")
          .select("id, start_date, end_date")
          .in("id", tripIds);

        const dateMap = new Map((tripDates ?? []).map((t: any) => [t.id, t]));
        const enrichedChats = chatsResult.data.map((c: any) => ({
          ...c,
          start_date: dateMap.get(c.trip_id)?.start_date ?? null,
          end_date: dateMap.get(c.trip_id)?.end_date ?? null,
        }));

        enrichedChats.sort(
          (a: any, b: any) =>
            new Date(a.start_date ?? 0).getTime() - new Date(b.start_date ?? 0).getTime()
        );

        setChats(enrichedChats);
      }

      if (!notifsResult.error && notifsResult.data) {
        setNotifications(
          notifsResult.data.map((n: any) => ({
            ...n,
            trip: Array.isArray(n.trip) ? n.trip[0] : n.trip,
            author: Array.isArray(n.author) ? n.author[0] : n.author,
          }))
        );
      }
      setLoading(false);
    };

    load();
  }, [user]);

  const markAsRead = async (id: string) => {
    await supabase.rpc("mark_notification_as_read", {
      notif_id: id,
      reader_id: user!.id,
    });
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <span className="text-slate-500 text-sm animate-pulse">Lade Nachrichten…</span>
      </div>
    );

  const unreadCount = notifications.length;
  const now = new Date();

  const activeChats = chats.filter(
    (c) => !c.end_date || new Date(c.end_date).getTime() + ARCHIVE_MS >= now.getTime()
  );
  const archivedChats = chats.filter(
    (c) => c.end_date && new Date(c.end_date).getTime() + ARCHIVE_MS < now.getTime()
  );

  const renderChatItem = (chat: any) => (
    <div
      key={chat.trip_id}
      className="flex items-center p-4 cursor-pointer hover:bg-[#1e2639] transition-colors"
      onClick={() => navigate(`/chat/${chat.trip_id}`)}
    >
      {chat.cover_image_url ? (
        <img
          src={chat.cover_image_url}
          alt="Trip Bild"
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-[#1e2639] flex items-center justify-center text-slate-500 flex-shrink-0 text-xl">
          ✈️
        </div>
      )}
      <div className="flex-1 ml-4 min-w-0">
        <p className="text-base font-semibold text-slate-100 truncate">
          {chat.trip_name}
        </p>
        <p className={`text-sm mt-0.5 ${chat.unread_count > 0 ? "text-sky-400" : "text-slate-500"}`}>
          {chat.unread_count > 0 ? "Neue Nachrichten" : "Alles gelesen"}
        </p>
      </div>
      {chat.unread_count > 0 && (
        <span className="ml-2 bg-sky-500 text-white text-xs font-bold px-2 py-1 rounded-full flex-shrink-0">
          {chat.unread_count > 99 ? "99+" : chat.unread_count}
        </span>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-4xl mx-auto pt-20 pb-24 px-4 sm:px-6">
        <h1 className="text-2xl font-bold text-slate-100 mb-6">Nachrichten</h1>

        {/* Sektion 1: Reise-Benachrichtigungen */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              🔔 Reise-Benachrichtigungen
              {unreadCount > 0 && (
                <span className="ml-2 bg-sky-500 text-white text-[10px] px-1.5 py-0.5 rounded-full align-middle">
                  {unreadCount}
                </span>
              )}
            </h2>
            <Link
              to="/notifications"
              className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
            >
              Alle →
            </Link>
          </div>

          {notifications.length === 0 ? (
            <div className="bg-[#161b27] border border-[#2a3347] rounded-2xl p-6 text-center text-slate-500 text-sm">
              📭 Keine ungelesenen Benachrichtigungen.
            </div>
          ) : (
            <div className="divide-y divide-[#2a3347] bg-[#161b27] rounded-2xl border border-[#2a3347]">
              {notifications.map((n) => {
                const isImportant = n.importance === "important";
                return (
                  <div
                    key={n.id}
                    className={`p-4 ${
                      isImportant
                        ? "bg-red-950/20 border-l-2 border-red-800"
                        : "hover:bg-[#1e2639]"
                    } transition-colors`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {isImportant && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/40 text-red-400 border border-red-800 font-medium">
                              Wichtig
                            </span>
                          )}
                          {n.trip?.name && (
                            <span className="text-[10px] text-slate-500">
                              {n.trip.name}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm font-medium ${isImportant ? "text-red-200" : "text-slate-100"}`}>
                          {n.title}
                        </p>
                        <p className="text-sm text-slate-400 mt-0.5 line-clamp-2">
                          {n.message}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <p className="text-xs text-slate-600">
                            {n.author
                              ? `${n.author.first_name} ${n.author.last_name}`
                              : "Svevana"}{" "}
                            ·{" "}
                            {formatDistanceToNow(new Date(n.created_at), {
                              addSuffix: true,
                              locale: de,
                            })}
                          </p>
                          {n.trip_id && (
                            <Link
                              to={`/trip/${n.trip_id}`}
                              className="text-xs text-sky-400 hover:text-sky-300"
                              onClick={(e) => e.stopPropagation()}
                            >
                              Zur Reise →
                            </Link>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => markAsRead(n.id)}
                        className="text-slate-600 hover:text-slate-400 text-lg leading-none flex-shrink-0 mt-0.5 transition-colors"
                        title="Als gelesen markieren"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sektion 2: Chats */}
        <div className="mt-6 mb-6">
          <h2 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-3">
            💬 Chats
          </h2>

          {activeChats.length === 0 && archivedChats.length === 0 ? (
            <div className="bg-[#161b27] border border-[#2a3347] rounded-2xl p-6 text-center text-slate-500 text-sm">
              Noch keine Chats vorhanden.
            </div>
          ) : activeChats.length === 0 ? (
            <div className="bg-[#161b27] border border-[#2a3347] rounded-2xl p-6 text-center text-slate-500 text-sm">
              Keine aktiven Chats vorhanden.
            </div>
          ) : (
            <div className="divide-y divide-[#2a3347] bg-[#161b27] rounded-2xl border border-[#2a3347]">
              {activeChats.map(renderChatItem)}
            </div>
          )}

          {archivedChats.length > 0 && (
            <>
              <button
                onClick={() => setShowArchivedChats(!showArchivedChats)}
                className="w-full text-center text-xs text-slate-600 hover:text-slate-400 py-3 transition-colors"
              >
                {showArchivedChats
                  ? "Vergangene Reisen ausblenden ↑"
                  : `Vergangene Reisen anzeigen (${archivedChats.length}) ↓`}
              </button>
              {showArchivedChats && (
                <div className="divide-y divide-[#2a3347] bg-[#161b27] rounded-2xl border border-[#2a3347] opacity-60">
                  {archivedChats.map(renderChatItem)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
